import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { Vue } from 'vue-property-decorator'

@Module({
  namespaced: true
})
export default class ConstructionStore extends VuexModule {
  materialsLoaded = false
  materials: KwixeoConstructions.Materials = []
  technicalsLoaded = false
  technicals: KwixeoConstructions.Technicals = []
  visibilitiesLoaded = false
  visibilities: KwixeoConstructions.TechnicalVisibilities = []

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['Materials', 'Technicals', 'Visibilities']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })
  }

  // Sort a list alphabetically
  @Mutation
  orderList (param: { list: KwixeoInterface.GenericObject[]; key: string }): void {
    const key = param.key
    param.list.sort((a, b) => {
      const aKey = a[key].toLowerCase()
      const bKey = b[key].toLowerCase()
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }

  // Change the loaded state
  @Mutation
  setMaterialsLoaded (state = true): void {
    this.materialsLoaded = state
  }

  // Set the sale terms
  @Mutation
  setMaterials (materials: KwixeoConstructions.Materials): void {
    this.materials = materials
  }

  // Add or update a sale term
  @Mutation
  updateMaterial (material: KwixeoConstructions.Material): void {
    if (!this.materialsLoaded) {
      return
    }
    const index = this.materials.findIndex(value => value.id === material.id)
    if (index >= 0) {
      Vue.set(this.materials, index, material)
    } else {
      this.materials.push(material)
    }
  }

  // Remove a sale term
  @Mutation
  removeMaterial (material: KwixeoConstructions.Material): void {
    if (!this.materialsLoaded) {
      return
    }
    const index = this.materials.findIndex(value => value.id === material.id)
    if (index >= 0) {
      Vue.delete(this.materials, index)
    }
  }

  // Load the sale terms from api
  @Action
  async loadMaterials (force = false): Promise<void> {
    if (!this.materialsLoaded || force) {
      const response = await Vue.prototype.$kwConstructions.axios.get('/construction/material?per_page=500&sort=name')
      await this.context.commit('setMaterials', response.data.data)
      this.context.commit('materialsLoaded')
    }
  }

  @Action
  async loadMaterial (id: number): Promise<void> {
    if (!this.materialsLoaded) {
      return
    }
    const response = await Vue.prototype.$kwSales.axios.get('/construction/material/' + id)
    await this.context.commit('updateMaterial', response.data)
  }

  // Add or update a sale term
  @Action
  setMaterial (material: KwixeoConstructions.Material): void {
    this.context.commit('updateMaterial', material)
    if (this.materialsLoaded) {
      this.context.commit('orderList', { list: this.materials, key: 'name' })
    }
  }

  // Delete a sale term
  @Action
  deleteMaterial (material: KwixeoConstructions.Material): void {
    this.context.commit('removeMaterial', material)
  }


  // Change the loaded state
  @Mutation
  setTechnicalsLoaded (state = true): void {
    this.technicalsLoaded = state
  }

  // Set the sale terms
  @Mutation
  setTechnicals (technicals: KwixeoConstructions.Technicals): void {
    this.technicals = technicals
  }

  // Add or update a sale term
  @Mutation
  updateTechnical (technical: KwixeoConstructions.Technical): void {
    if (!this.technicalsLoaded) {
      return
    }
    const index = this.technicals.findIndex(value => value.id === technical.id)
    if (index >= 0) {
      Vue.set(this.technicals, index, technical)
    } else {
      this.technicals.push(technical)
    }
  }

  // Remove a sale term
  @Mutation
  removeTechnical (technical: KwixeoConstructions.Technical): void {
    if (!this.technicalsLoaded) {
      return
    }
    const index = this.technicals.findIndex(value => value.id === technical.id)
    if (index >= 0) {
      Vue.delete(this.technicals, index)
    }
  }

  // Load the sale terms from api
  @Action
  async loadTechnicals (force = false): Promise<void> {
    if (!this.technicalsLoaded || force) {
      const response = await Vue.prototype.$kwConstructions.axios.get('/construction/technical?per_page=500&sort=name')
      await this.context.commit('setTechnicals', response.data.data)
      this.context.commit('setTechnicalsLoaded')
    }
  }

  @Action
  async loadTechnical (id: number): Promise<void> {
    if (!this.technicalsLoaded) {
      return
    }
    const response = await Vue.prototype.$kwSales.axios.get('/construction/technical/' + id)
    await this.context.commit('updateTechnical', response.data)
  }

  // Add or update a sale term
  @Action
  setTechnical(technical: KwixeoConstructions.Technical): void {
    this.context.commit('updateTechnical', technical)
    if (this.technicalsLoaded) {
      this.context.commit('orderList', { list: this.technicals, key: 'name' })
    }
  }

  // Delete a sale term
  @Action
  deleteTechnical (technical: KwixeoConstructions.Technical): void {
    this.context.commit('removeTechnical', technical)
  }

  get allTechnicals (): KwixeoConstructions.TechnicalElements {
    const technicals: KwixeoConstructions.TechnicalElements = this.technicals.map(technical => ({
      type: 'technical',
      id: technical.id,
      name: technical.name
    }))

    this.context.rootState.globalDatas.teamUsers
      .filter((user: KwixeoFramework.TeamUser) => user.roles?.includes('technical') && user.status.active)
      .forEach((user: KwixeoFramework.TeamUser) => {
        technicals.push({
          type: 'teamUser',
          id: user.id,
          name: user.fullname
        })
      })

    return technicals.sort((a, b) => {
      const aKey = a.name.toLowerCase()
      const bKey = b.name.toLowerCase()
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }


  // Change the loaded state
  @Mutation
  setVisibilitiesLoaded (state = true): void {
    this.visibilitiesLoaded = state
  }

  // Set the sale terms
  @Mutation
  setVisibilities (visibilities: KwixeoConstructions.TechnicalVisibilities): void {
    this.visibilities = visibilities
  }


  // Set the sale terms
  @Mutation
  setVisibility (visibility: KwixeoConstructions.TechnicalVisibility): void {
    if (!this.visibilitiesLoaded) {
      return
    }

    const value = this.visibilities.find(val => val.element_id === visibility.element_id && val.element_type === visibility.element_type)
    if (value) {
      value.state = visibility.state
    } else {
      this.visibilities.push(visibility)
    }
  }

  @Action
  async loadVisibilities (force = false): Promise<void> {
    if (!this.visibilitiesLoaded || force) {
      const response = await Vue.prototype.$kwConstructions.axios.get('/construction/technical/visibility')
      await this.context.commit('setVisibilities', response.data)
      this.context.commit('setVisibilitiesLoaded')
    }
  }

  @Action
  updateVisibility (visibility: KwixeoConstructions.TechnicalVisibility): void {
    this.context.commit('setVisibility', visibility)
  }
}
